import { ProcessStep } from "../constants";

export type Locale = 'en' | 'de';
export const DEFAULT_LOCALE: Locale = 'de';

export interface ProcessStepTranslationKeys {
    readonly label: string,
    readonly summary: string,
    readonly back_tooltip: string,
    readonly next_tooltip: string,
    readonly help_content: string,
}

export const TRANSLATIONS: {[id: string]: {[id: string]: string}} = {
    de: {
        'ok': 'Ok',
        'cancel': 'Abbrechen',
        'help': 'Hilfe',
        'show_help': 'Hilfe zeigen',
        'undo_label': 'Rückgängig',
        'undo': 'Rückgängig machen',
        'redo': 'Wiederherstellen',
        'back': 'Zurück',
        'next': 'Weiter',
        'more_info': 'Mehr Infos',
        'back_to_overview': 'Zurück zur Übersicht',
        'step_brainstorm_label': 'Ideen sammeln',
        'step_brainstorm_summary': 'Sammeln Sie alles, was Ihnen zu Ihrer Präsentation einfällt.',
        'step_brainstorm_next_tooltip': 'Weiter zum Schritt "Ziele definieren"',
        'step_brainstorm_help_content': '',
        'step_basic_data_label': 'Ziele definieren',
        'step_basic_data_summary': 'Beantworten Sie einige grundlegende Fragen, um den Rahmen Ihrer Präsentation festzulegen.',
        'step_basic_data_back_tooltip': 'Zurück zum Schritt "Brainstorming"',
        'step_basic_data_next_tooltip': 'Weiter zum Schritt "Gliedern"',
        'step_basic_data_help_content': '',
        'step_structure_label': 'Inhalte strukturieren',
        'step_structure_summary': 'Filtern Sie die besten Ideen heraus und strukturieren Sie sie.',
        'step_structure_back_tooltip': 'Zurück zum Schritt "Ziele definieren"',
        'step_structure_next_tooltip': 'Weiter zum Schritt "Skizzieren"',
        'step_structure_help_content': '',
        'step_sketch_label': 'Folien skizzieren',
        'step_sketch_summary': 'Erstellen Sie grobe Entwürfe für Ihre Folien. Probieren Sie mehrere Varianten aus.',
        'step_sketch_back_tooltip': 'Zurück zum Schritt "Glieder"',
        'step_sketch_next_tooltip': 'Weiter zum Schritt "Exportieren"',
        'step_sketch_help_content': '',
        'step_export_label': 'Entwurf exportieren',
        'step_export_summary': 'Exportieren Sie die Struktur Ihrer Präsentation und die Folienentwürfe in Ihr Präsentationstool.',
        'step_export_back_tooltip': 'Zurück zum Schritt "Skizzieren"',
        'step_export_help_content': '',
        'add_note_label': 'Haftzettel hinzufügen',
        'add_yellow_note_tooltip': 'Gelben Haftzettel hinzufügen',
        'add_pink_note_tooltip': 'Pinken Haftzettel hinzufügen',
        'add_green_note_tooltip': 'Grünen Haftzettel hinzufügen',
        'add_orange_note_tooltip': 'Orangen Haftzettel hinzufügen',
        'add_slide_tooltip': 'Folie hinzufügen',
        'zoom_in_tooltip': 'Leinwand vergössern',
        'zoom_out_tooltip': 'Leinwand verkleinern',
        'zoom_tooltip': 'Vergrösserung der Leinwand anpassen',
        'select_a_template_tooltip': 'Template auswählen',
        'select_sketch_label': 'Zeichenmodus',
        'select_sketch_none_tooltip': 'Nicht zeichnen',
        'select_sketch_line_tooltip': 'Linie zeichnen',
        'select_sketch_arrow_tooltip': 'Pfeil zeichnen',
        'select_sketch_rect_tooltip': 'Rechteck oder Quadrat zeichnen',
        'select_sketch_ellipse_tooltip': 'Ellipse oder Kreis zeichnen',
        'select_sketch_freehand_tooltip': 'Freihand zeichnen',
        'select_sketch_text_tooltip': 'Text einfügen',
        'basic_data_title': 'Ziele definieren',
        'basic_data_subtitle': 'Erläuterungen der Fragen finden Sie in der Hilfe auf der rechten Seite',
        'basic_data_goals_label': 'Was möchten Sie erreichen?',
        'basic_data_main_message_label': 'Wie lautet die Kernaussage?',
        'basic_data_audience_label': 'Wer ist das Publikum?',
        'button_hide_basic_data_label': 'Verstecke Basisdaten',
        'select_template_title': 'Template auswählen',
        'number_of_main_sections': 'Zahl der Hauptpunkte',
        'select_three_main_sections_tooltip': 'Vorlage mit 3 Hauptpunkten wählen',
        'select_four_main_sections_tooltip': 'Vorlage mit 4 Hauptpunkten wählen',
        'select_five_main_sections_tooltip': 'Vorlage mit 5 Hauptpunkten wählen',
        'text_shape_dialog_title': 'Text eingeben',
        'text_shape_dialog_label': 'Bitte geben Sie den anzuzeigenden Text ein',
        'slide_notes_dialog_title': 'Notizen',
        'slide_notes_dialog_label': 'Bitte geben Sie die Notizen ein',
        'time_dialog_title': 'Geschätzte Dauer eintragen',
        'time_dialog_content': 'Bitte geben Sie die ungefähre Zeit an, die Sie für diesen Teil der Präsentation verwenden möchten',
        'time_dialog_label': 'Geschätzte Dauer',
        'note_dialog_pick_color': 'Bitte wählen Sie eine Farbe:',
        'introduction_dialog_title': 'Überblick',
        'notes_headline': 'Notizen',
        'error_page_title': 'Unerwarteter Fehler',
        'error_page_text_1': 'Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es später noch einmal.',
        'error_page_text_2': 'Wenn Sie uns einen Fehlerbericht senden, werden wir uns so bald wie möglich um eine Behebung kümmern.',
        'error_page_send_report': 'Fehlerbericht senden',
        'error_page_reload': 'Neu laden',
    },
    en: {
        'ok': 'Ok',
        'cancel': 'Cancel',
        'undo': 'Undo',
        'redo': 'Redo',
        'back': 'Back',
        'next': 'Next',
        'step1_label': 'Basic Data',
        'step1_summary': 'Lay the foundation of your presentation:',
        'step2_label': 'Brainstorm',
        'step2_summary': 'Collect everything that comes to your mind about your presentation.',
        'step3_label': 'Choose template',
        'step3_summary': 'Choose a template for your presentation.',
        'step4_label': 'Structure',
        'step4_summary': 'Filter out your best ideas and structure them.',
        'step5_label': 'Sketch',
        'step5_summary': 'Create rough sketches of your slides. Try several approaches.',
        'step6_label': 'Export',
        'step6_summary': 'Export the structure of your presentation and the sketches of your slides to your favorite presentation tool.',
        'add_yellow_note_tooltip': 'Add yellow note',
        'add_pink_note_tooltip': 'Add pink note',
        'add_green_note_tooltip': 'Add green note',
        'add_orange_note_tooltip': 'Add orange note',
        'select_a_template_tooltip': 'Select a template',
        'basic_data_dialog_title': 'Define basic data',
        'basic_data_goal_label': 'What is your goal?',
        'basic_data_main_message_label': 'What is your main message?',
        'basic_data_audience_label': 'Who is your audience?',
        'button_hide_basic_data_label': 'Hide basic data',
        'select_template_title': 'Select a template',
        'time_dialog_title': 'Enter estimated time',
        'time_dialog_content': 'Please enter the estimated time, that you want to spend on this part of the presentation',
        'time_dialog_label': 'Estimated time',
    }
};

async function readTextFile(locale: Locale, key: string) {
    const response = await fetch(`${process.env.PUBLIC_URL}/text/${key}.${locale}.md`);
    return await response.text();
}

async function readTextFiles(locale: Locale) {
    TRANSLATIONS[locale].introduction_dialog_content = await readTextFile(locale, 'introduction_dialog');
    TRANSLATIONS[locale].basic_data_subtitle = await readTextFile(locale, 'basic_data_introduction');
    TRANSLATIONS[locale].step_brainstorm_help_content = await readTextFile(locale, 'step_brainstorm_help');
    TRANSLATIONS[locale].step_basic_data_help_content = await readTextFile(locale, 'step_basic_data_help');
    TRANSLATIONS[locale].step_structure_help_content = await readTextFile(locale, 'step_structure_help');
    TRANSLATIONS[locale].step_sketch_help_content = await readTextFile(locale, 'step_sketch_help');
    TRANSLATIONS[locale].step_export_help_content = await readTextFile(locale, 'step_export_help');
}

export async function setupTranslationStore() {
    await readTextFiles('de');
}

export function getTranslations(locale: Locale): {[id: string]: string} {
    return TRANSLATIONS[locale] || TRANSLATIONS[DEFAULT_LOCALE] || {};
}

export function getTranslation(locale: Locale, id: string): string {
    return getTranslations(locale)[id];
}

export function getProcessStepTranslations(processStep: ProcessStep): ProcessStepTranslationKeys {
    switch (processStep) {
        default:
            return {
                label: 'step_brainstorm_label',
                summary: 'step_brainstorm_summary',
                back_tooltip: '',
                next_tooltip: 'step_brainstorm_next_tooltip',
                help_content: 'step_brainstorm_help_content',
            };
        case ProcessStep.BASIC_DATA:
            return {
                label: 'step_basic_data_label',
                summary: 'step_basic_data_summary',
                back_tooltip: 'step_basic_data_back_tooltip',
                next_tooltip: 'step_basic_data_next_tooltip',
                help_content: 'step_basic_data_help_content',
            };
        case ProcessStep.STRUCTURE:
            return {
                label: 'step_structure_label',
                summary: 'step_structure_summary',
                back_tooltip: 'step_structure_back_tooltip',
                next_tooltip: 'step_structure_next_tooltip',
                help_content: 'step_structure_help_content',
            };
        case ProcessStep.SKETCH:
            return {
                label: 'step_sketch_label',
                summary: 'step_sketch_summary',
                back_tooltip: 'step_sketch_back_tooltip',
                next_tooltip: 'step_sketch_next_tooltip',
                help_content: 'step_sketch_help_content',
            };
        case ProcessStep.EXPORT:
            return {
                label: 'step_export_label',
                summary: 'step_export_summary',
                back_tooltip: 'step_export_back_tooltip',
                next_tooltip: '',
                help_content: 'step_export_help_content',
            };
    }
}


// WEBPACK FOOTER //
// ./src/services/translation-store.ts